import { DatePicker, DateValue } from '@mantine/dates';
import { FC } from 'react';

import { DayjsUnit } from '@/models/Dayjs.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { getWeekRangeFromDate } from '@/utils/date-range-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

const CalendarDatePicker: FC = () => {
    // Only get the date range fields we need
    const is_week_view_enabled = useCalendarStore((state) => state.is_week_view_enabled);
    const start_date = useFilterStore((state) => state.start_date);
    const end_date = useFilterStore((state) => state.end_date);
    const is_weekend_visible = useCalendarStore((state) => state.is_weekend_visible);
    const min_date = dayjs().subtract(3, DayjsUnit.MONTH).toDate();

    // Calculate current value directly from store with proper type assertions
    const current_value = is_week_view_enabled
        ? ([start_date || undefined, end_date || undefined] as [DateValue, DateValue])
        : start_date || undefined;

    const handleDateChange = (new_value: DateValue | [DateValue, DateValue]): void => {
        if (is_week_view_enabled) {
            const [start_date, end_date] = new_value as [DateValue, DateValue];

            if (start_date && !end_date) {
                const [final_start_date, final_end_date] = getWeekRangeFromDate(
                    new Date(start_date),
                    is_weekend_visible
                );

                // Ensure we don't try to set undefined dates
                if (final_start_date && final_end_date) {
                    useFilterStore.setState({
                        start_date: final_start_date,
                        end_date: final_end_date,
                    });
                }
                return;
            }

            if (start_date && end_date) {
                useFilterStore.setState({
                    start_date: new Date(start_date),
                    end_date: new Date(end_date),
                });
                return;
            }
            return;
        }

        const selected_date = new_value as DateValue;
        if (!selected_date) return;

        useFilterStore.setState({
            start_date: new Date(selected_date),
            end_date: new Date(selected_date),
        });
    };

    return (
        <DatePicker
            locale="de"
            key={start_date?.getTime()}
            type={is_week_view_enabled ? 'range' : 'default'}
            value={current_value}
            defaultDate={start_date || undefined}
            allowDeselect={false}
            onChange={handleDateChange}
            minDate={min_date}
            size="sm"
            highlightToday
        />
    );
};

export default CalendarDatePicker;
